import { useEffect, useRef, useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { LinkIcon, MailCheckIcon, MailXIcon } from 'lucide-react';
import { AuthShell, BrandMark, Button, ResultState, Spinner } from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import { env } from '@/app/env';
import { authClient } from '@/lib/auth-client';

type Phase = 'verifying' | 'done' | 'failed';

/**
 * Where the self-signup confirmation email lands.
 *
 * The verify call marks the address confirmed and does nothing else — it
 * deliberately opens no session, because a link sitting in an inbox (or a
 * forwarded email) must not be enough to log someone in. The user signs in
 * with their password next, and the app shell's subscription gate takes them
 * to the plan picker from there.
 *
 * Any session already on this machine is left alone for the same reason.
 */
export function VerifyEmailPage() {
  const { t } = useTranslation();
  const search = useSearch({ from: '/_public/verify-email' });
  const [phase, setPhase] = useState<Phase>(search.token ? 'verifying' : 'failed');
  const started = useRef(false);

  useEffect(() => {
    const token = search.token;
    if (!token || started.current) return;
    started.current = true;

    void authClient
      .verifyEmail({ query: { token } })
      .then(({ error }) => setPhase(error ? 'failed' : 'done'));
  }, [search.token]);

  const brand = <BrandMark name={env.appName} tagline={t('app.tagline')} />;

  if (phase === 'verifying') {
    return (
      <AuthShell brand={brand} title={t('auth.verifyingTitle')}>
        <div className="flex items-center gap-3 py-4 text-sm text-muted-foreground">
          <Spinner /> {t('auth.verifyingBody')}
        </div>
      </AuthShell>
    );
  }

  if (phase === 'done') {
    return (
      <AuthShell brand={brand} title={t('auth.verifiedTitle')}>
        <ResultState
          tone="success"
          icon={<MailCheckIcon />}
          description={t('auth.verifiedBody')}
          className="px-0 py-2"
          action={
            <Button asChild>
              <Link to="/login">{t('auth.signIn')}</Link>
            </Button>
          }
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell brand={brand} title={t('auth.verifyFailedTitle')}>
      <ResultState
        tone="warning"
        icon={search.token ? <MailXIcon /> : <LinkIcon />}
        description={t('auth.verifyFailedBody')}
        className="px-0 py-2"
        action={
          <Button asChild>
            <Link to="/login">{t('auth.signIn')}</Link>
          </Button>
        }
      />
    </AuthShell>
  );
}
